import type { AppStore } from "@/store/AppStore";
import type {
  AiToolkitAssistantFollowUp,
  SearchAssistantFollowUpsInput,
  SearchAssistantFollowUpsOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  searchAssistantFollowUpsInputSchema,
  searchAssistantFollowUpsOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import {
  compareIsoStringsAscending,
  DEFAULT_AI_TOOLKIT_SEARCH_LIMIT,
  matchesAiToolkitSearchQuery,
  normalizeAiToolkitFilterValue,
  normalizeAiToolkitSearchQuery,
} from "@/modules/ai-toolkit/tools/utils/local-search-helpers";
import {
  buildAssistantFollowUpFromCreateChangeRequestUpdateValue,
  buildAssistantFollowUpFromSyncUpdateValue,
  isCreateFollowUpChangeRequestUpdateValue,
} from "@/modules/ai-toolkit/tools/assistant-follow-ups/assistant-follow-up-tools.helpers";

const matchesSearchInput = ({
  followUp,
  input,
  query,
}: {
  followUp: AiToolkitAssistantFollowUp;
  input: SearchAssistantFollowUpsInput;
  query: string | undefined;
}): boolean => {
  const assistantId = normalizeAiToolkitFilterValue(input.assistantId);
  const projectId = normalizeAiToolkitFilterValue(input.projectId);
  const taskId = normalizeAiToolkitFilterValue(input.taskId);
  const sourceContextId = normalizeAiToolkitFilterValue(input.sourceContextId);
  const status = normalizeAiToolkitFilterValue(input.status);
  const scheduledFrom = normalizeAiToolkitFilterValue(input.scheduledFrom);
  const scheduledTo = normalizeAiToolkitFilterValue(input.scheduledTo);

  if (assistantId && followUp.assistantId !== assistantId) return false;
  if (projectId && followUp.projectId !== projectId) return false;
  if (taskId && followUp.taskId !== taskId) return false;
  if (sourceContextId && followUp.sourceContextId !== sourceContextId) return false;
  if (status && followUp.status !== status) return false;
  if (scheduledFrom && followUp.scheduledFor < scheduledFrom) return false;
  if (scheduledTo && followUp.scheduledFor > scheduledTo) return false;

  return matchesAiToolkitSearchQuery({
    query,
    values: [followUp.content, followUp.status, followUp.commitmentKind, followUp.triggerType],
  });
};

export const searchAssistantFollowUps = async ({
  store,
  input,
}: {
  store: AppStore;
  input: SearchAssistantFollowUpsInput;
}): Promise<SearchAssistantFollowUpsOutput> => {
  const query = normalizeAiToolkitSearchQuery(input.query);
  const [followUpRows, changeRequestRows] = await Promise.all([
    store.assistantFollowUps.localTable.toArray(),
    store.assistantFollowUpChangeRequests.localTable.toArray(),
  ]);
  const realFollowUpIds = new Set(followUpRows.map(followUpRow => followUpRow.model_id));

  const followUps = [
    ...followUpRows.map(followUpRow =>
      buildAssistantFollowUpFromSyncUpdateValue({ updateValue: followUpRow })
    ),
    ...changeRequestRows
      .filter(changeRequestRow => !realFollowUpIds.has(changeRequestRow.model_id))
      .filter(changeRequestRow =>
        isCreateFollowUpChangeRequestUpdateValue({ updateValue: changeRequestRow })
      )
      .map(changeRequestRow =>
        buildAssistantFollowUpFromCreateChangeRequestUpdateValue({
          updateValue: changeRequestRow,
        })
      ),
  ]
    .filter(followUp => matchesSearchInput({ followUp, input, query }))
    .sort(
      (left, right) =>
        compareIsoStringsAscending({
          left: left.scheduledFor,
          right: right.scheduledFor,
        }) || left.id.localeCompare(right.id)
    )
    .slice(0, input.limit ?? DEFAULT_AI_TOOLKIT_SEARCH_LIMIT);

  return { followUps };
};

export const searchAssistantFollowUpsTool: AiToolkitToolDefinition<
  AiToolkitToolName.SearchAssistantFollowUps,
  SearchAssistantFollowUpsInput,
  SearchAssistantFollowUpsOutput
> = {
  name: AiToolkitToolName.SearchAssistantFollowUps,
  inputSchema: searchAssistantFollowUpsInputSchema,
  outputSchema: searchAssistantFollowUpsOutputSchema,
  strict: true,
  execute: searchAssistantFollowUps,
};
